import type { JobAction, JobReasonInput } from '@chukua/common';
import { useState } from 'react';
import { ApiError } from '../../../shared/shared.api';
import { apiErrorMessage } from '../../../shared/policies/api-error-message';
import { useToast } from '../../../shared/hooks/use-toast';
import { cancelJob, closeJob, confirmJob, holdJob, quoteJob, resumeJob, type JobDetail } from '../jobs.api';

/** confirm's own success toast (gitaumoses4/chukua#37) — it's the one action whose outcome branches, so it can't share ACTION_PAST_TENSE's fixed verb. */
function confirmToastMessage(number: string, status: string): string {
  return status === 'on_hold' ? `Job ${number} flagged for credit review — on hold` : `Job ${number} confirmed`;
}

const ACTION_PAST_TENSE: Record<JobAction, string> = {
  quote: 'quoted',
  confirm: 'confirmed',
  cancel: 'cancelled',
  hold: 'held',
  resume: 'resumed',
  close: 'closed',
};

/** The default refusal text for a 400 on an action that has one, used only when the server sends no `message` of its own. */
const BLOCKED_ACTION_FALLBACK: Partial<Record<JobAction, string>> = {
  confirm: "can't be confirmed yet",
  quote: "can't be quoted yet",
};

/**
 * A job's state actions (gitaumoses4/chukua#39) — one call per action, matching the routes
 * `job-state.policy.ts` exposes. A 409 (`InvalidJobTransitionError`/`StaleJobStatusError`,
 * the job moved since the page loaded) reloads and shows an inline alert instead of a toast,
 * so the caller isn't left looking at a stale state (A4); a 400 on `confirm` (a missing PO number)
 * or on `quote` (no agreed price yet) shows inline too, each with its own refusal text next to the
 * form that fixes it; any other failure toasts and leaves the record as it was, the same as a row
 * action elsewhere in the app.
 */
export function useJobActions(id: string, reload: () => Promise<void>) {
  const [conflictError, setConflictError] = useState<string | null>(null);
  const [confirmBlockedError, setConfirmBlockedError] = useState<string | null>(null);
  const [quoteBlockedError, setQuoteBlockedError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<JobAction | null>(null);
  const toast = useToast();

  // confirm (a missing PO number, gitaumoses4/chukua#33) and quote (no agreed price yet) each have their own
  // 400 the office can fix — shown inline, next to the form that fixes it, rather than as a generic toast.
  const blockedErrorSetters: Partial<Record<JobAction, (message: string) => void>> = {
    confirm: setConfirmBlockedError,
    quote: setQuoteBlockedError,
  };

  async function run<T>(action: JobAction, call: () => Promise<T>, job: Pick<JobDetail, 'number'>, successMessage: (result: T) => string): Promise<void> {
    setConflictError(null);
    setConfirmBlockedError(null);
    setQuoteBlockedError(null);
    setPendingAction(action);
    try {
      const result = await call();
      await reload();
      toast.success(successMessage(result));
    } catch (err) {
      const setBlockedError = blockedErrorSetters[action];
      if (err instanceof ApiError && err.status === 409) {
        await reload();
        setConflictError("This job's state changed since the page loaded — showing the latest.");
      } else if (err instanceof ApiError && err.status === 400 && setBlockedError) {
        setBlockedError(apiErrorMessage(err) ?? `Job ${job.number} ${BLOCKED_ACTION_FALLBACK[action]}.`);
      } else {
        toast.error(`Could not ${action} job ${job.number} — try again.`);
      }
    } finally {
      setPendingAction(null);
    }
  }

  function runVoid(action: JobAction, call: () => Promise<void>, job: Pick<JobDetail, 'number'>): Promise<void> {
    return run(action, call, job, () => `Job ${job.number} ${ACTION_PAST_TENSE[action]}`);
  }

  return {
    conflictError,
    confirmBlockedError,
    quoteBlockedError,
    pendingAction,
    quote: (job: JobDetail, validUntil: string) => runVoid('quote', () => quoteJob(job.id, { validUntil }), job),
    confirm: (job: JobDetail) =>
      run(
        'confirm',
        () => confirmJob(job.id),
        job,
        (result) => confirmToastMessage(job.number, result.status),
      ),
    resume: (job: JobDetail) => runVoid('resume', () => resumeJob(job.id), job),
    close: (job: JobDetail) => runVoid('close', () => closeJob(job.id), job),
    cancel: (job: JobDetail, input: JobReasonInput) => runVoid('cancel', () => cancelJob(job.id, input), job),
    hold: (job: JobDetail, input: JobReasonInput) => runVoid('hold', () => holdJob(job.id, input), job),
  };
}
